import type { JSONSchema } from 'json-schema-typed';
import DB from '../db/DB';
import type { RenderTemplatesWithName } from '../ReplicantChatbot';
import type { ComputedProperties } from '../ReplicantComputedProperties';
import { ReplicantConfig } from '../ReplicantConfig';
import type { ScheduledActions } from '../ReplicantScheduledActions';
import SB from '../SchemaBuilder';
import { computedPropertiesToQuerySchema } from '../utils/computedPropertiesToQuerySchema';
import { deepMerge } from '../utils/ObjUtils';
import { schemaBuilderToJSONSchema } from '../utils/schemaBuilderToJSONSchema';
import { APIError } from './APIError';
import { adminAPISchema } from './ServerReplicantAdmin';
import { ECSTasksMap, TaskArgs, TaskRunnerClient, taskSchemas, TaskType } from './TaskRunner';

type ApiResponseAdminTask = {
    id: string;
    task: TaskType;
    args: object;
    createdAt: number;
    ecsTasks: ECSTasksMap;
    itemsProcessed: number;
};

export default class ServerReplicantAdminTasks {
    availableTasks: { name: TaskType; params: JSONSchema }[];

    constructor(
        private opts: {
            config: ReplicantConfig<any>;
            dateNow: () => number;
            db: DB<any>;
            taskRunnerClient: TaskRunnerClient;
        },
    ) {
        this.availableTasks = [
            { name: 'chatbotBroadcast', params: makeChatbotBroadcastParamsJSONSchema(opts.config) },
            { name: 'reindex', params: {} },
            { name: 'reindexIndexed', params: makeReindexIndexedParamsJSONSchema(opts.config) },
            { name: 'scheduleActionToMany', params: makeScheduledActionToManyParamsJSONSchema(opts.config) },
        ];
    }

    async handleListTasks() {
        const tasks = await this.opts.db.adminTasks.listTasks();
        const ecsTaskIds = tasks.reduce<string[]>((acc, task) => [...acc, ...task.ecsIds], []);
        const ecsTasks = ecsTaskIds.length > 0 ? await this.opts.taskRunnerClient.getTasks(ecsTaskIds) : {};

        // List each active task...
        const activeTasks = await Promise.all(
            tasks.map(async ({ args, createdAt, ecsIds, id, task }) => {
                // ...and aggregate of worker progresses for each task:
                const workerProgresses = await this.opts.db.adminTasks.listWorkerProgresses({ taskId: id });
                const itemsProcessed = workerProgresses.reduce((acc, progress) => acc + progress.itemsProcessed, 0);

                const activeTask: ApiResponseAdminTask = {
                    args,
                    createdAt,
                    id,
                    task,
                    ecsTasks: Object.assign({}, ...ecsIds.map((ecsId) => ({ [ecsId]: ecsTasks[ecsId] }))),
                    itemsProcessed,
                };

                return activeTask;
            }),
        );

        return {
            availableTasks: this.availableTasks,
            activeTasks,
        };
    }

    async handleRunTask(opts: SB.ExtractType<typeof adminAPISchema.runTask>) {
        const id = this.opts.dateNow() + Math.floor(1e13 + Math.random() * 1e13).toString();

        return this.runTask({ args: opts.args, id, task: opts.task });
    }

    async handleStopTask(opts: SB.ExtractType<typeof adminAPISchema.stopTask>) {
        const task = await this.opts.db.adminTasks.getTask(opts.id);
        if (!task) {
            throw new APIError('Task not found', { statusCode: 404, statusDescription: 'Not Found' });
        }

        await Promise.all(
            task.ecsIds.map((ecsTaskId) =>
                this.opts.taskRunnerClient.stopTask(ecsTaskId).catch(() => {
                    // Swallow error to avoid failing the other stopTask calls
                }),
            ),
        );
    }

    async handleRestartTask(opts: SB.ExtractType<typeof adminAPISchema.restartTask>) {
        const task = await this.opts.db.adminTasks.getTask(opts.id);
        if (!task) {
            throw new APIError('Task not found', { statusCode: 404, statusDescription: 'Not Found' });
        }

        const ecsTasks = await this.opts.taskRunnerClient.getTasks(task.ecsIds);
        for (const ecsTaskId in ecsTasks) {
            const { status } = ecsTasks[ecsTaskId]!;
            if (status !== 'STOPPED') {
                throw new APIError(`Cannot restart non-stopped ECS Task ${ecsTaskId}: ${status}`, {
                    statusCode: 409,
                    statusDescription: 'Conflict',
                });
            }
        }

        return this.runTask({ args: task.args, id: task.id, task: task.task });
    }

    private async runTask<T extends TaskType>(opts: {
        args: TaskArgs<T>;
        id: string;
        task: T;
    }): Promise<ApiResponseAdminTask> {
        const ecsTasks = await this.opts.taskRunnerClient.runTask({
            args: opts.args,
            id: opts.id,
            task: opts.task,
        });

        const { args, createdAt, id, task } = await this.opts.db.adminTasks.createTask({
            id: opts.id,
            ecsIds: Object.keys(ecsTasks),
            args: opts.args,
            task: opts.task,
            now: this.opts.dateNow(),
        });

        return { args, createdAt, id, task, ecsTasks, itemsProcessed: 0 };
    }
}

function makeChatbotBroadcastParamsJSONSchema(replicantConfig: {
    adminTool?: { chatbotMessageTemplates?: RenderTemplatesWithName<any> };
    computedProperties?: ComputedProperties;
}): JSONSchema {
    const taskParamsSchema = schemaBuilderToJSONSchema(taskSchemas.chatbotBroadcast);
    delete taskParamsSchema.default;

    // Extend the default task arguments schema with chatbot message template names and argument schemas from Replicant configuration:
    const { assets, ...templates } = replicantConfig.adminTool?.chatbotMessageTemplates ?? {};

    const anyOf = Object.keys(templates).map((templateName): JSONSchema => {
        const templateArgsSchema = templates[templateName]!.argsSchema;
        const templateHasArgs = templateArgsSchema._type !== 'unknown';

        return {
            title: templateName,
            properties: {
                messageTemplateName: {
                    const: templateName,
                    default: templateName,
                    description:
                        "Name of the chatbot message template. Define the available templates in the game's `adminTool.chatbotMessageTemplates` Replicant configuration property.",
                },
                messageTemplateArgs: {
                    ...(templateHasArgs ? schemaBuilderToJSONSchema(templateArgsSchema) : undefined),
                    description: 'Chatbot message template arguments.',
                },
            },
            required: templateHasArgs ? ['messageTemplateArgs', 'messageTemplateName'] : ['messageTemplateName'],
        };
    });

    // Overwrite `messageTemplateName` and `messageTemplateArgs` properties with `anyOf`:
    delete taskParamsSchema.properties!.messageTemplateName;
    delete taskParamsSchema.properties!.messageTemplateArgs;
    taskParamsSchema.anyOf = anyOf;

    // Use computed properties to generate the `where` property schema:
    const computedProperties = replicantConfig.computedProperties ?? {};
    delete taskParamsSchema.properties?.where;
    const schema = deepMerge(computedPropertiesToQuerySchema(computedProperties), taskParamsSchema);

    // Add description fields that show up as UI labels in the CSTool form:
    const fullSchema = taskSchemas.chatbotBroadcast.getFullSchema();
    const paramDescriptions: { [key in keyof typeof fullSchema]?: string } = {
        count: 'Number of parallel ECS Task instances.',
        concurrency: 'Number of concurrent workers per ECS Task instance.',
        payload: 'Payload attached to the chatbot message entry point. Enter a JSON-encoded object.',
        forceChannel:
            'Force the chatbot message to be sent to a specific channel. Use "nativepush" for push notifications.',
    };

    for (const [key, description] of Object.entries(paramDescriptions)) {
        const property = schema.properties?.[key];
        if (typeof property === 'object') {
            property.description = description;
        }
    }

    return schema;
}

function makeReindexIndexedParamsJSONSchema(replicantConfig: { sharedStates?: Record<string, unknown> }): JSONSchema {
    const taskParamsSchema = schemaBuilderToJSONSchema(taskSchemas.reindexIndexed);
    delete taskParamsSchema.default;

    // Add description fields that show up as UI labels in the CSTool form:
    taskParamsSchema.description = 'Reindex all the currently indexed players.';

    const fullSchema = taskSchemas.reindexIndexed.getFullSchema();
    const paramDescriptions: { [key in keyof typeof fullSchema]?: string } = {
        count: 'Number of parallel ECS Task instances.',
        concurrency: 'Number of concurrent workers per ECS Task instance.',
        dbReadCapacity: 'Maximum DynamoDB Read Capacity Units consumed per second.',
        easing: 'Milliseconds spent at task start to linearly increase rate limit from 0 to maximum.',
        esPushCapacity: 'Maximum number of index writes per second.',
        sharedStateIndex: 'The shared state to reindex. Leave undefined to reindex the main player index.',
    };

    for (const [key, description] of Object.entries(paramDescriptions)) {
        const property = taskParamsSchema.properties?.[key];

        if (typeof property === 'object') {
            property.description = description;
        }
    }

    // Populate `sharedStateIndex` enum values with the names of the configured shared states:
    const sharedStateIndexProperty = taskParamsSchema.properties?.sharedStateIndex;

    if (!sharedStateIndexProperty || typeof sharedStateIndexProperty !== 'object') {
        throw Error('Missing `sharedStateIndex` from task parameters schema');
    }

    sharedStateIndexProperty.enum = Object.keys(replicantConfig.sharedStates ?? {});

    return taskParamsSchema;
}

function makeScheduledActionToManyParamsJSONSchema(replicantConfig: {
    computedProperties?: ComputedProperties;
    scheduledActions?: ScheduledActions;
}): JSONSchema {
    // Extend the default task arguments schema with scheduled action names and argument schemas from Replicant configuration:
    const scheduledActions = replicantConfig.scheduledActions ?? {};

    const anyOf = Object.keys(scheduledActions).map((actionName): JSONSchema => {
        const actionArgsSchema = scheduledActions[actionName]!.schema as SB.Schema;
        const actionHasArgs = actionArgsSchema._type !== 'unknown';

        return {
            title: actionName,
            properties: {
                actionName: { const: actionName, default: actionName, description: 'Scheduled action name.' },
                actionArgs: actionHasArgs
                    ? {
                          ...schemaBuilderToJSONSchema(actionArgsSchema),
                          description: 'Scheduled action arguments.',
                      }
                    : {},
            },
            required: actionHasArgs ? ['actionArgs', 'actionName'] : ['actionName'],
        };
    });

    const taskParamsSchema = schemaBuilderToJSONSchema(taskSchemas.scheduleActionToMany);

    // Overwrite `actionName` and `actionArgs` properties with `anyOf`:
    delete taskParamsSchema.properties!.actionName;
    delete taskParamsSchema.properties!.actionArgs;
    taskParamsSchema.anyOf = anyOf;

    // Use computed properties to generate the `where` property schema:
    delete taskParamsSchema.properties!.where;
    const schema = deepMerge(
        computedPropertiesToQuerySchema(replicantConfig.computedProperties ?? {}),
        taskParamsSchema,
    );
    delete schema.additionalProperties;

    // Add description fields that show up as UI labels in the CSTool form:
    const fullSchema = taskSchemas.scheduleActionToMany.getFullSchema();
    const paramDescriptions: { [key in keyof typeof fullSchema]?: string } = {
        count: 'Number of parallel ECS Task instances.',
        concurrency: 'Number of concurrent workers per ECS Task instance.',
        delayMs:
            'Scheduled action delay in milliseconds. Each action gets scheduled to the time slot of `task start time + delayMs`. Leave undefined to schedule action to the next available time slot.',
        notificationId:
            'Scheduled action notification ID. Use an identical notification ID to reschedule a previously scheduled action.',
    };

    for (const [key, description] of Object.entries(paramDescriptions)) {
        const property = schema.properties?.[key];
        if (typeof property === 'object') {
            property.description = description;
        }
    }

    return schema;
}
